import { contextSelectionDigestValue } from "./contextSelection.ts";
import { sha256 } from "@noble/hashes/sha2";
import { bytesToHex } from "@noble/hashes/utils";
import { parseContextSelectionExpressions } from "./contextSelection.ts";
import type { BoardDefaultSelection, ContextSelectionExpression } from "@cleo/contracts";
import { workingContextMemberIds } from "@cleo/contracts";

/**
 * Arc SEL (register BD-CAN-13, 2026-09-08): the selection is a stored set.
 *
 * The World keeps one stored set of member references per session. This
 * module is the Desk-side twin of backend/convex/lib/worldSessionSelection.js:
 * the same receipt shape, the same canonical digest body, byte for byte.
 * An inline set binds its exact members; a set above the inline ceiling that
 * a verified birth manifest carries binds the manifest identity, whose own
 * digest binds the items (the RL21 law for frozen resolutions).
 */
export const WORLD_SESSION_SELECTION_SCHEMA_VERSION = "world-session-selection-v1";
/** One selectionMove door call admits this many references; lockstep with backend/convex/lib/worldSessionSelection.js. */
export const SELECTION_MOVE_ITEMS_MAX = 2048;

const SHA256_HEX = /^[0-9a-f]{64}$/u;

export interface WorldSessionSelectionManifest {
  readonly manifestId: string;
  readonly manifestDigest: string;
  readonly itemCount: number;
}

export interface WorldSessionSelectionBirth {
  readonly status: "pending" | "ready";
  readonly cut: number;
  readonly readyAt?: number;
  readonly boardKey?: string;
  readonly policy?: BoardDefaultSelection;
}

export interface WorldSessionSelection {
  readonly schemaVersion: typeof WORLD_SESSION_SELECTION_SCHEMA_VERSION;
  readonly worldKey: string;
  readonly roomId: string;
  readonly sessionId: string;
  readonly agentEnvironmentId: string;
  readonly agentThreadId: string;
  /** The sequence of the last move applied to this set (the birth at first). */
  readonly selectionCut: number;
  readonly selectionDigest: string;
  /** Sorted, unique member references. Empty is a real, typed selection. */
  readonly memberIds: ReadonlyArray<string>;
  readonly expressions?: ReadonlyArray<ContextSelectionExpression>;
  readonly expressionCount?: number;
  readonly workingContextBitmap?: string;
  readonly memberManifest?: WorldSessionSelectionManifest;
  readonly birth?: WorldSessionSelectionBirth;
  /**
   * SESSION LAW (principal, 15 September 2026): anything generated in a
   * session stays in the session. What this thread created, as the World
   * records it: reachable for the rest of the session without a move.
   */
  readonly createdMemberIds?: ReadonlyArray<string>;
}

export type WorldSessionSelectionMembers = (
  | { readonly memberIds: ReadonlyArray<string> }
  | { readonly memberManifest: WorldSessionSelectionManifest }
) & {
  readonly workingContextBitmap?: string;
  readonly expressions?: ReadonlyArray<ContextSelectionExpression>;
};

/** The address a consumer knows; the room may be unknown and comes back in the receipt. */
export interface SessionSelectionAddress {
  readonly worldKey: string;
  readonly roomId?: string | null;
  readonly sessionId: string;
  readonly agentEnvironmentId: string;
  readonly agentThreadId: string;
}

export function sessionSelectionBody(
  worldKey: string,
  sessionId: string,
  cut: number,
  members: WorldSessionSelectionMembers,
): string {
  const value =
    "memberIds" in members
      ? { memberIds: members.memberIds }
      : {
          memberManifest: {
            manifestId: members.memberManifest.manifestId,
            manifestDigest: members.memberManifest.manifestDigest,
            itemCount: members.memberManifest.itemCount,
          },
        };
  return JSON.stringify({
    schemaVersion: WORLD_SESSION_SELECTION_SCHEMA_VERSION,
    worldKey,
    sessionId,
    cut,
    ...value,
    workingContextBitmap: members.workingContextBitmap,
    expressions: contextSelectionDigestValue(members.expressions),
  });
}

export function digestSessionSelection(
  worldKey: string,
  sessionId: string,
  cut: number,
  members: WorldSessionSelectionMembers,
): string {
  return bytesToHex(
    sha256(new TextEncoder().encode(sessionSelectionBody(worldKey, sessionId, cut, members))),
  );
}

export interface WorldSessionSelectionHead {
  readonly roomId: string;
  readonly selectionCut: number;
  readonly selectionDigest: string;
  readonly count: number;
  readonly chunkCount: number;
  readonly expressions?: ReadonlyArray<ContextSelectionExpression>;
  readonly expressionCount?: number;
  readonly workingContextBitmap?: string;
  readonly memberManifest?: WorldSessionSelectionManifest;
  readonly birth?: WorldSessionSelectionBirth;
  /** SESSION LAW: members this thread created, carried on the head so a chunked set keeps them. */
  readonly createdMemberIds?: ReadonlyArray<string>;
}

export type WorldSessionSelectionReceipt =
  | { readonly kind: "inline"; readonly selection: WorldSessionSelection }
  | { readonly kind: "chunked"; readonly head: WorldSessionSelectionHead };

const text = (value: unknown, max = 256): string | null =>
  typeof value === "string" && value.trim().length > 0 && value.length <= max ? value : null;

function manifestOf(value: unknown): WorldSessionSelectionManifest | null | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const manifest = value as Record<string, unknown>;
  const manifestId = text(manifest["manifestId"]);
  const manifestDigest = manifest["manifestDigest"];
  const itemCount = manifest["itemCount"];
  if (
    manifestId === null ||
    typeof manifestDigest !== "string" ||
    !SHA256_HEX.test(manifestDigest) ||
    !Number.isSafeInteger(itemCount) ||
    (itemCount as number) < 0
  )
    return null;
  return { manifestId, manifestDigest, itemCount: itemCount as number };
}

function birthOf(value: unknown): WorldSessionSelectionBirth | null | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const birth = value as Record<string, unknown>;
  if (
    (birth["status"] !== "ready" && birth["status"] !== "pending") ||
    !Number.isSafeInteger(birth["cut"]) ||
    (birth["cut"] as number) < 1
  )
    return null;
  if (
    birth["status"] === "ready" &&
    (!Number.isSafeInteger(birth["readyAt"]) ||
      (birth["readyAt"] as number) < (birth["cut"] as number))
  )
    return null;
  const policy = birth["policy"] as Record<string, unknown> | undefined;
  if (
    policy !== undefined &&
    (typeof policy !== "object" ||
      policy === null ||
      Array.isArray(policy) ||
      !["all", "none", "groups", "standing"].includes(String(policy["mode"])) ||
      ["groups", "memberTypes", "tags"].some(
        (key) =>
          policy[key] !== undefined &&
          (!Array.isArray(policy[key]) ||
            (policy[key] as unknown[]).some((item) => typeof item !== "string")),
      ) ||
      (policy["query"] !== undefined && typeof policy["query"] !== "string"))
  )
    return null;
  if (birth["status"] === "pending" && (!policy || text(birth["boardKey"]) === null)) return null;
  return birth as unknown as WorldSessionSelectionBirth;
}

function exactSortedMembers(value: unknown): ReadonlyArray<string> | null {
  if (!Array.isArray(value)) return null;
  const members: string[] = [];
  for (const entry of value) {
    if (typeof entry !== "string" || entry.length === 0 || entry.length > 512) return null;
    if (members.length > 0 && entry <= members[members.length - 1]!) return null;
    members.push(entry);
  }
  return members;
}

/**
 * Parse one World-door receipt for the stored set. The receipt's own
 * coordinate must match the address the caller asked for; the room comes
 * from the receipt when the caller did not know it. An inline receipt is
 * digest-verified here; a chunked head is verified once its chunks are
 * assembled through `assembleWorldSessionSelection`.
 */
export function parseWorldSessionSelectionReceipt(
  value: unknown,
  address: SessionSelectionAddress,
): WorldSessionSelectionReceipt | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const receipt = value as Record<string, unknown>;
  if (receipt["schemaVersion"] !== WORLD_SESSION_SELECTION_SCHEMA_VERSION) return null;
  const roomId = text(receipt["roomId"]);
  if (
    roomId === null ||
    receipt["worldKey"] !== address.worldKey ||
    receipt["sessionId"] !== address.sessionId ||
    receipt["agentEnvironmentId"] !== address.agentEnvironmentId ||
    receipt["agentThreadId"] !== address.agentThreadId ||
    (typeof address.roomId === "string" && address.roomId.length > 0 && address.roomId !== roomId)
  )
    return null;
  const selectionCut = receipt["selectionCut"];
  const selectionDigest = receipt["selectionDigest"];
  if (
    !Number.isSafeInteger(selectionCut) ||
    (selectionCut as number) < 0 ||
    typeof selectionDigest !== "string" ||
    !SHA256_HEX.test(selectionDigest)
  )
    return null;
  const memberManifest = manifestOf(receipt["memberManifest"]);
  if (memberManifest === null) return null;
  const birth = birthOf(receipt["birth"]);
  if (birth === null) return null;
  const count = receipt["count"];
  if (!Number.isSafeInteger(count) || (count as number) < 0) return null;
  const bitmap = receipt["workingContextBitmap"];
  if (bitmap !== undefined && typeof bitmap !== "string") return null;
  const expressions =
    receipt["expressions"] === undefined
      ? undefined
      : parseContextSelectionExpressions(receipt["expressions"]);
  if (expressions === null) return null;
  const expressionCount = receipt["expressionCount"];
  if (
    expressionCount !== undefined &&
    (!Number.isSafeInteger(expressionCount) || (expressionCount as number) < 0)
  )
    return null;
  if (expressions !== undefined && expressions.length !== expressionCount) return null;
  const delivery = {
    ...(bitmap === undefined ? {} : { workingContextBitmap: bitmap }),
    ...(expressions === undefined ? {} : { expressions }),
    ...(expressionCount === undefined ? {} : { expressionCount: expressionCount as number }),
  };
  const createdMemberIds =
    receipt["createdMemberIds"] === undefined
      ? undefined
      : exactSortedMembers(receipt["createdMemberIds"]);
  if (createdMemberIds === null) return null;
  const created =
    createdMemberIds === undefined || createdMemberIds.length === 0 ? {} : { createdMemberIds };
  if (receipt["memberIds"] === undefined) {
    const chunkCount = receipt["chunkCount"];
    if (!Number.isSafeInteger(chunkCount) || (chunkCount as number) < 0) return null;
    return {
      kind: "chunked",
      head: {
        roomId,
        selectionCut: selectionCut as number,
        selectionDigest,
        count: count as number,
        chunkCount: chunkCount as number,
        ...delivery,
        ...(memberManifest === undefined ? {} : { memberManifest }),
        ...(birth === undefined ? {} : { birth }),
        ...created,
      },
    };
  }
  const memberIds = exactSortedMembers(receipt["memberIds"]);
  if (memberIds === null || memberIds.length !== count) return null;
  const selection = assembleWorldSessionSelection(
    address,
    {
      roomId,
      selectionCut: selectionCut as number,
      selectionDigest,
      count: count as number,
      chunkCount: 0,
      ...delivery,
      ...(memberManifest === undefined ? {} : { memberManifest }),
      ...(birth === undefined ? {} : { birth }),
    },
    memberIds,
  );
  if (selection === null) return null;
  return {
    kind: "inline",
    selection:
      createdMemberIds === undefined || createdMemberIds.length === 0
        ? selection
        : { ...selection, createdMemberIds },
  };
}

/** One chunk page of a set above the inline ceiling. */
export function parseWorldSessionSelectionChunk(
  value: unknown,
  head: WorldSessionSelectionHead,
  chunkIndex: number,
): {
  readonly memberIds: ReadonlyArray<string>;
  readonly expressions?: ReadonlyArray<ContextSelectionExpression>;
} | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const page = value as Record<string, unknown>;
  if (
    page["schemaVersion"] !== WORLD_SESSION_SELECTION_SCHEMA_VERSION ||
    page["selectionCut"] !== head.selectionCut ||
    page["selectionDigest"] !== head.selectionDigest ||
    page["chunkIndex"] !== chunkIndex ||
    !Array.isArray(page["memberIds"]) ||
    page["memberIds"].some((entry) => typeof entry !== "string" || entry.length === 0)
  )
    return null;
  const expressions =
    page["expressions"] === undefined
      ? undefined
      : parseContextSelectionExpressions(page["expressions"]);
  if (expressions === null || (head.expressionCount !== undefined) !== (expressions !== undefined))
    return null;
  return {
    memberIds: page["memberIds"] as ReadonlyArray<string>,
    ...(expressions === undefined ? {} : { expressions }),
  };
}

/** Verify the digest of an assembled set and mint the receipt the Desk copies. */
export function assembleWorldSessionSelection(
  address: SessionSelectionAddress,
  head: WorldSessionSelectionHead,
  memberIds: ReadonlyArray<string>,
  expressions = head.expressions,
): WorldSessionSelection | null {
  const delivery =
    head.workingContextBitmap === undefined
      ? {}
      : { workingContextBitmap: head.workingContextBitmap };
  if (
    expressions !== undefined &&
    (expressions.length !== head.expressionCount ||
      parseContextSelectionExpressions(expressions) === null)
  )
    return null;
  if ((head.expressionCount !== undefined) !== (expressions !== undefined)) return null;
  if (
    expressions !== undefined &&
    JSON.stringify(
      [
        ...new Set(
          expressions.filter((choice) => choice.selected).map((choice) => choice.memberId),
        ),
      ].sort(),
    ) !== JSON.stringify(memberIds)
  )
    return null;
  const choices = expressions === undefined ? {} : { expressions };
  const members: WorldSessionSelectionMembers = {
    ...choices,
    ...(head.memberManifest === undefined
      ? { memberIds }
      : { memberManifest: head.memberManifest }),
    ...delivery,
  };
  if (new Set(memberIds).size !== memberIds.length) return null;
  if (workingContextMemberIds({ memberIds, ...delivery }) === null) return null;
  if (
    digestSessionSelection(address.worldKey, address.sessionId, head.selectionCut, members) !==
    head.selectionDigest
  )
    return null;
  if (head.memberManifest !== undefined && memberIds.length !== head.memberManifest.itemCount)
    return null;
  return {
    schemaVersion: WORLD_SESSION_SELECTION_SCHEMA_VERSION,
    worldKey: address.worldKey,
    roomId: head.roomId,
    sessionId: address.sessionId,
    agentEnvironmentId: address.agentEnvironmentId,
    agentThreadId: address.agentThreadId,
    selectionCut: head.selectionCut,
    selectionDigest: head.selectionDigest,
    memberIds: [...memberIds].sort(),
    ...choices,
    ...delivery,
    ...(head.memberManifest === undefined ? {} : { memberManifest: head.memberManifest }),
    ...(head.birth === undefined ? {} : { birth: head.birth }),
    ...(head.createdMemberIds === undefined || head.createdMemberIds.length === 0
      ? {}
      : { createdMemberIds: head.createdMemberIds }),
  };
}
